'use client'

import React, { useState, useCallback, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useAuth } from '@/hooks/useAuth'
import styles from './styles.module.scss'
import clsx from 'clsx'

interface HeaderNavigationMenuProps {
  transition?: string
}

const HeaderNavigationMenu: React.FC<HeaderNavigationMenuProps> = ({
  transition = 'fade-transition',
}) => {
  const { isAuthenticated, logout } = useAuth()
  const [noAnimation, setNoAnimation] = useState(true)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const menu = {
    settings: [
      {
        name: 'プロフィール',
        param: 'profile',
      },
      {
        name: 'メール設定',
        param: 'notification',
      },
    ],
    other: [
      {
        name: 'よくある質問',
        link: '/faq',
      },
    ],
  }

  const linkToParam = useCallback((param: string) => {
    const url = `/members/profile?type=${param}`
    window.location.href = url
  }, [])

  const handleMenuClick = useCallback(
    (close: () => void) => {
      if (noAnimation) setNoAnimation(false)

      if (isMenuOpen) {
        setIsClosing(true)
        setTimeout(() => {
          setIsClosing(false)
          setIsMenuOpen(false)
          close()
        }, 400)
      } else {
        setIsMenuOpen(true)
      }
    },
    [noAnimation, isMenuOpen],
  )

  const handleLogout = useCallback(async () => {
    await logout()
  }, [logout])

  const handleCloseMenu = useCallback(() => {
    if (isMenuOpen) {
      setIsClosing(true)
      setTimeout(() => {
        setIsClosing(false)
        setIsMenuOpen(false)
      }, 300)
    }
  }, [isMenuOpen])

  // メニューの外側をクリックして閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMenuOpen && menuRef.current && event.target instanceof Element) {
        if (!menuRef.current.contains(event.target)) {
          handleCloseMenu()
        }
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen, handleCloseMenu])

  if (!isAuthenticated) {
    return null
  }

  return (
    <div className={styles.headerMenu} ref={menuRef}>
      <div>
        <button
          className={clsx(
            styles.menuTrigger,
            { [styles.active]: isMenuOpen },
            { [styles.noAnimation]: noAnimation },
          )}
          onClick={() => handleMenuClick(() => setIsMenuOpen(false))}
          aria-label='Menu'
        >
          <span />
          <span />
          <span />
        </button>

        {isMenuOpen && (
          <div className={clsx(styles.navMenu, styles.gnavMenu, { [styles.closing]: isClosing })}>
            {/* 設定 */}
            <p className={styles.memberMenuTitle}>
              <span className={styles.menuTitleInner}>設定</span>
            </p>
            <ul className={styles.memberMenu}>
              {menu.settings.map((item) => (
                <li
                  key={item.name}
                  className={styles.memberMenuItem}
                  onClick={() => linkToParam(item.param)}
                >
                  <span className={styles.memberMenuLink}>{item.name}</span>
                </li>
              ))}
            </ul>

            {/* その他 */}
            <p className={styles.memberMenuTitle}>
              <span className={styles.menuTitleInner}>その他</span>
            </p>
            <ul className={styles.memberMenu}>
              {menu.other.map((item) => (
                <li key={item.name} className={styles.memberMenuItem}>
                  <Link
                    href={item.link}
                    className={styles.memberMenuLink}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* ログアウト */}
            <button type='button' className={styles.menuFooter} onClick={handleLogout}>
              ログアウト
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default HeaderNavigationMenu
